<template>
  <div class="container ts-search-wrap">
    <div class="ts-search shadow-lg">
      <div class="ts-search-toolbar d-flex justify-content-between align-items-center gap-3 mb-3">
        <div class="nav nav-pills ts-search-modes d-inline-flex gap-1 rounded-pill p-1 mw-100" role="tablist">
          <button
            v-for="item in modes"
            :id="`jobSearchMode-${item.mode}`"
            :key="item.mode"
            class="nav-link ts-mode-button d-inline-flex align-items-center justify-content-center"
            :class="{ active: mode === item.mode }"
            type="button"
            role="tab"
            :aria-selected="String(mode === item.mode)"
            aria-controls="jobSearchPane"
            @click="mode = item.mode"
          >
            <i class="bi" :class="item.icon" aria-hidden="true"></i>
            {{ item.label }}
          </button>
        </div>
        <button
          v-b-toggle.jobs-filter
          class="btn btn-link ts-advanced-search d-none d-lg-inline-flex align-items-center flex-shrink-0 px-0 py-0"
          type="button"
          @click="openFilter"
        >
          <i class="bi bi-sliders me-2" aria-hidden="true"></i>
          {{ translations[13939] }}
        </button>
      </div>

      <div id="jobSearchPane" class="ts-search-content" role="tabpanel" :aria-labelledby="`jobSearchMode-${mode}`">
        <form @submit.prevent="submit">
          <!-- mode 'ki': one free-text field, the AI extracts profession, location and radius -->
          <div v-if="mode === 'ki'" class="ts-search-fields ts-search-fields--single">
            <div class="ts-search-fields-wrapper flex-grow-1">
              <label class="visually-hidden" for="jobSearchAi">{{ aiPlaceholder }}</label>
              <div class="input-group">
                <span class="input-group-text ts-search-icon"><i class="bi bi-search" aria-hidden="true"></i></span>
                <input id="jobSearchAi" v-model="aiSearchText" class="form-control" type="search" :placeholder="aiPlaceholder" />
              </div>
            </div>
            <button class="btn btn-primary ts-search-submit fw-bold" type="submit" :disabled="aiLoading">
              <span v-if="aiLoading" class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
              {{ translations[13938] }}
            </button>
          </div>

          <!-- modes 'fields' / 'groups' / 'fulltext': what + where -->
          <div v-else class="ts-search-fields">
            <div class="ts-search-field-pair">
              <div class="ts-search-field">
                <!-- mode 'fields': one occupational field (cluster) -->
                <ts-field-select
                  v-if="mode === 'fields'"
                  id="jobSearchField"
                  v-model="fieldId"
                  :items="clusters"
                  :placeholder="translations[13588]"
                  :unit="translations[13439]"
                />
                <!-- mode 'groups': occupational groups (professions), several possible -->
                <ts-occupation-select v-else-if="mode === 'groups'" v-model="selectProfession" />
                <!-- mode 'fulltext': keyword, job title or company -->
                <template v-else>
                  <label class="visually-hidden" for="jobSearchText">{{ translations[10648] }}</label>
                  <div class="input-group">
                    <span class="input-group-text ts-search-icon"><i class="bi bi-search" aria-hidden="true"></i></span>
                    <input
                      id="jobSearchText"
                      v-model="searchText"
                      class="form-control"
                      type="search"
                      :placeholder="translations[10431]"
                      @input="selectProfession = null"
                    />
                  </div>
                </template>
              </div>
              <div class="ts-search-field">
                <!-- Radius search only on the groupCurses sites, the others pick just a place -->
                <select-location-radius v-if="isCurses" v-model="selectLocation" :radius.sync="radius" :placeholder="locationPlaceholder" />
                <select-location v-else v-model="selectLocation" :placeholder="locationPlaceholder" :lg="true" />
              </div>
            </div>
            <button class="btn btn-primary ts-search-submit fw-bold" type="submit">{{ translations[13938] }}</button>
          </div>

          <!-- Below lg the modes need the whole toolbar row, so "Erweiterte Suche" sits under the field -->
          <div class="d-flex d-lg-none justify-content-center mt-2">
            <button v-b-toggle.jobs-filter class="btn btn-link ts-advanced-search px-0 py-0" type="button" @click="openFilter">
              <i class="bi bi-sliders me-2" aria-hidden="true"></i>
              {{ translations[13939] }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Watch } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import AiJobSearchMixin from '@/mixins/AiJobSearchMixin';
import { AppModule } from '@/store/modules/app';
import { ProfessionModule } from '@/store/modules/profession';
import { JobType } from '@/types/ProfessionType';
import { applyClusterJobSearch, jobClusters } from '@/sections/jobs/search';
import TsOccupationSelect from '@/components/Talentspace/search/TsOccupationSelect.vue';
import SelectLocationRadius from '@/components/Widget/SelectLocationRadius.vue';
import SelectLocation from '@/components/Widget/SelectLocation.vue';
import { groupCurses } from '@/config/configuration';
import TsFieldSelect from '@/components/Talentspace/search/TsFieldSelect.vue';

type SearchMode = 'ki' | 'fields' | 'groups' | 'fulltext';

// Job search of the welcome page and of the jobs page. All state lives in
// ProfessionModule as before; the card only changes how the search is entered.
@Component({
  components: {
    TsOccupationSelect, SelectLocationRadius, SelectLocation, TsFieldSelect,
  },
})
export default class TsJobsSearchCard extends mixins(AiJobSearchMixin) {
  mode: SearchMode = 'fields';

  aiSearchText = '';

  aiLoading = false;

  fieldId: number | null = null;

  get translations() {
    return AppModule.translation;
  }

  get isAiEnabled() {
    return Number(AppModule.setting?.enable_ai_jobs_search) === 1;
  }

  get modes() {
    const modes: { mode: SearchMode, icon: string, label: string }[] = [
      { mode: 'fields', icon: 'bi-briefcase', label: AppModule.translation[16578] },
      { mode: 'groups', icon: 'bi-list-check', label: AppModule.translation[921] },
      { mode: 'fulltext', icon: 'bi-search', label: this.translations[10434] || 'Volltext' },
    ];
    // AI search only where the portal has it switched on (PAT: enable_ai_jobs_search)
    if (this.isAiEnabled) modes.unshift({ mode: 'ki', icon: 'bi-stars', label: this.translations[15926] || 'KI-Stellensuche' });
    return modes;
  }

  // Getters are not available while vue-class-component collects data
  created() {
    if (this.isAiEnabled) this.mode = 'ki';
  }

  // Portal settings may arrive after the first render: start in AI mode once it turns out to be on
  @Watch('isAiEnabled')
  onAiEnabled(enabled: boolean) {
    if (enabled && this.mode === 'fields' && !this.fieldId) this.mode = 'ki';
  }

  get clusters() {
    return jobClusters();
  }

  get aiPlaceholder() {
    return AppModule.settingPortal.ai_search_hint || this.translations[15928];
  }

  get isCurses() {
    return groupCurses(AppModule.sid);
  }

  // sids 'aba'/'abatest' keep the plain "place" hint instead of the portal's search hint
  get locationPlaceholder() {
    if (['aba', 'abatest'].includes(AppModule.sid)) return `${this.translations[13589]}...`;
    return AppModule.settingPortal.search_input_jobs_placeholder || `${this.translations[9503]}...`;
  }

  // ─── search state (ProfessionModule) ─────────────────────────────────────────

  get selectProfession() {
    return ProfessionModule.searchJobProfession;
  }

  set selectProfession(value: any) {
    ProfessionModule.SetSearchJobProfession(value);
    ProfessionModule.SetSearchJobProfessionTitle(value);
    ProfessionModule.SetSearch({ obj: 'profession_id', value: value?.map((v: any) => v.id).join(',') || null });
  }

  get selectLocation() {
    return ProfessionModule.searchJobLocation;
  }

  set selectLocation(value: any) {
    ProfessionModule.SetSearchJobLocation(value);
    ProfessionModule.SetSearchJobLocationTitle(value);
    ProfessionModule.SetSearch({ obj: 'location_id', value: value?.id || null });
  }

  get searchText() {
    return ProfessionModule.jobSearch.q;
  }

  // Full-text search also shows the term as a removable chip above the results
  set searchText(value) {
    ProfessionModule.SetSearch({ obj: 'q', value });
    ProfessionModule.SetSearchTags({ tag: 'textTag', value });
    ProfessionModule.SetSearch({ obj: 'qmode', value: 2 });
  }

  get radius() {
    return ProfessionModule.jobSearch.radius;
  }

  set radius(value) {
    ProfessionModule.SetSearch({ obj: 'radius', value });
    ProfessionModule.SetSearchTags({ tag: 'radius', value });
  }

  // ─── actions ─────────────────────────────────────────────────────────────────

  openFilter() {
    ProfessionModule.SetJobType(JobType.search);
  }

  submit() {
    if (this.mode === 'ki') {
      this.searchAi();
    } else if (this.mode === 'fields' && this.fieldId) {
      this.searchField(this.fieldId);
    } else {
      this.search();
    }
  }

  search() {
    ProfessionModule.SET_FILTER_DEFAULT();
    ProfessionModule.SET_SEARCH_DEFAULT();
    ProfessionModule.SET_PARAMS_DEFAULT();
    ProfessionModule.SET_TAGS_DEFAULT();
    ProfessionModule.SetProfessionTitle('');
    ProfessionModule.SetJobType(JobType.search);
    AppModule.SetVisibleMap(false);
    AppModule.SetVisibleJobs(true);
    AppModule.SetVisibleClusterJobs(false);
    AppModule.SET_WELCOME(true);
    this.goToJobs();
  }

  searchField(id: number) {
    const cluster = this.clusters.find((item) => item.id === id);
    // The cluster search starts from clean params, which also drops the place: keep the chosen one
    const location = this.selectLocation;
    const { radius } = this;
    applyClusterJobSearch({ id, title: cluster?.name || '' }, true);
    if (location) {
      this.selectLocation = location;
      this.radius = radius;
    }
    this.goToJobs();
  }

  async searchAi() {
    this.aiLoading = true;
    try {
      // navigates to the jobs page itself
      await this.applyAiFilters(this.aiSearchText, true);
    } finally {
      this.aiLoading = false;
    }
  }

  goToJobs() {
    this.$router.push({ name: 'Jobs', query: { lang: AppModule.language } }).catch(() => {});
  }
}
</script>
